import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Form, InputGroup, Modal, Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { FaDoorClosed, FaTimes, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [doorSearchQuery, setDoorSearchQuery] = useState('');
  const [doorSearchResults, setDoorSearchResults] = useState([]);
  const [searchingDoors, setSearchingDoors] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Search for doors by item number
  const handleDoorSearch = async (e) => {
    e?.preventDefault();
    
    if (!doorSearchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setSearchingDoors(true);
      setShowSearchModal(true);
      
      const { supabase } = await import('../config/supabase');
      
      const { data: doorsData, error: doorsError } = await supabase
        .from('door_metadata')
        .select(`
          *,
          folders!inner(
            id,
            name,
            project_id,
            projects!inner(
              id,
              name,
              project_number,
              status,
              due_date
            )
          )
        `)
        .ilike('item_nr', `%${doorSearchQuery}%`);
      
      if (doorsError) throw doorsError;
      
      // Group results by project
      const projectMap = new Map();
      
      doorsData.forEach(door => {
        const projectId = door.folders.projects.id;
        
        if (!projectMap.has(projectId)) {
          projectMap.set(projectId, {
            projectId,
            projectName: door.folders.projects.name,
            projectNumber: door.folders.projects.project_number,
            projectStatus: door.folders.projects.status,
            doors: []
          });
        }
        
        projectMap.get(projectId).doors.push({
          folderId: door.folders.id,
          folderName: door.folders.name,
          itemNr: door.item_nr,
          barcodeUrl: door.barcode_url,
          width: door.width,
          height: door.height,
          amount: door.amount
        });
      });
      
      const results = Array.from(projectMap.values());
      setDoorSearchResults(results);
      
      toast.success(`Found ${doorsData.length} door(s) in ${results.length} project(s)`);
    } catch (error) {
      console.error('Error searching doors:', error);
      toast.error('Failed to search doors');
      setDoorSearchResults([]);
    } finally {
      setSearchingDoors(false);
    }
  };

  const clearSearch = () => {
    setDoorSearchQuery('');
    setDoorSearchResults([]);
    setShowSearchModal(false);
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'On Hold': return 'warning';
      default: return 'secondary';
    }
  };

  return (
    <Navbar bg="dark" variant="dark" expand="lg">
      <Container>
        <Navbar.Brand as={Link} to={user ? "/admin/dashboard" : "/"} className="d-flex align-items-center">
          <img
            src="/logo.png"
            alt="Logo"
            height="30"
            className="me-2"
          />
          Hestia Drawing Center
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          {user && (
            <Form onSubmit={handleDoorSearch} className="d-flex mx-auto" style={{ maxWidth: '400px' }}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <FaDoorClosed />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search doors by item number..."
                  value={doorSearchQuery}
                  onChange={(e) => setDoorSearchQuery(e.target.value)}
                  disabled={searchingDoors}
                  style={{ fontSize: '0.875rem' }}
                />
                {doorSearchQuery && (
                  <Button 
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearSearch}
                    disabled={searchingDoors}
                  >
                    <FaTimes />
                  </Button>
                )}
                <Button 
                  type="submit" 
                  variant="outline-light"
                  size="sm"
                  disabled={searchingDoors || !doorSearchQuery.trim()}
                >
                  {searchingDoors ? <Spinner animation="border" size="sm" /> : <FaSearch />}
                </Button>
              </InputGroup>
            </Form>
          )}
          <Nav className="ms-auto">
            {user ? (
              <>
                <Nav.Link as={Link} to="/admin/dashboard">Dashboard</Nav.Link>
                <Nav.Link as={Link} to="/admin/clients">Clients</Nav.Link>
                {profile?.is_super_admin && (
                  <Nav.Link as={Link} to="/admin/users">User Management</Nav.Link>
                )}
                <Nav.Link as={Link} to="/admin/profile">Profile</Nav.Link>
                <Nav.Item className="d-flex align-items-center ms-2">
                  <span className="text-light me-2">
                    Welcome, {profile?.username || user.email}
                    {profile?.is_super_admin && <span className="badge bg-danger ms-1">Super Admin</span>}
                  </span>
                  <Button variant="outline-light" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </Nav.Item>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/admin/login">Admin Login</Nav.Link>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>

      {/* Search Results Modal */}
      <Modal 
        show={showSearchModal} 
        onHide={() => setShowSearchModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaDoorClosed className="me-2" />
            Door Search Results
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {searchingDoors ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Searching...</p>
            </div>
          ) : doorSearchResults.length === 0 ? (
            <Alert variant="info">
              No projects found with doors matching "{doorSearchQuery}"
            </Alert>
          ) : (
            <div>
              <Alert variant="success">
                Found <strong>{doorSearchResults.reduce((sum, p) => sum + p.doors.length, 0)} door(s)</strong> in <strong>{doorSearchResults.length} project(s)</strong>
              </Alert>
              
              {doorSearchResults.map((result, idx) => (
                <Card key={idx} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{result.projectNumber}</strong> - {result.projectName}
                      <span className={`badge bg-${getBadgeColor(result.projectStatus)} ms-2`}>
                        {result.projectStatus}
                      </span>
                    </div>
                    <Button 
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        navigate(`/admin/project/${result.projectId}`);
                        setShowSearchModal(false);
                      }}
                    >
                      View Project
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-2">
                      <strong>{result.doors.length}</strong> matching door(s)
                    </p>
                    <Row>
                      {result.doors.map((door, doorIdx) => (
                        <Col md={6} lg={4} key={doorIdx} className="mb-3">
                          <Card className="h-100">
                            <Card.Body>
                              <h6 className="text-truncate">
                                <FaDoorClosed className="me-2" />
                                {door.folderName}
                              </h6>
                              <div className="mb-2">
                                <small><strong>Item:</strong></small>
                                <br />
                                <code className="bg-light px-2 py-1 rounded d-inline-block mt-1 small">
                                  {door.itemNr}
                                </code>
                              </div>
                              <div className="mb-2">
                                <small><strong>Size:</strong> {door.width} × {door.height} mm</small>
                              </div>
                              <div className="mb-2">
                                <small><strong>Qty:</strong> {door.amount}</small>
                              </div>
                              {door.barcodeUrl && (
                                <div className="text-center mt-2">
                                  <img 
                                    src={door.barcodeUrl} 
                                    alt="Barcode"
                                    style={{ 
                                      maxWidth: '100%', 
                                      height: 'auto', 
                                      border: '1px solid #dee2e6', 
                                      borderRadius: '4px', 
                                      padding: '4px',
                                      background: 'white'
                                    }}
                                  />
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSearchModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Navbar>
  );
};

export default Header;
